import { ReactNode } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { AlertCircle, Lock, ArrowLeft } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./alert";
import { Button } from "./button";
import { Link } from "wouter";

interface PermissionPageProps {
  /** Single permission required */
  permission?: string;
  /** Multiple permissions - user must have ANY of these */
  anyOf?: string[];
  /** Multiple permissions - user must have ALL of these */
  allOf?: string[];
  /** Page content to render when permission check passes */
  children: ReactNode;
  /** Page title for the access denied message */
  pageTitle?: string;
}

/**
 * Wrap a page to require permissions before rendering.
 * Shows an access denied message if the user doesn't have the required permissions.
 *
 * @example
 * // Require a single permission
 * <PermissionPage permission="quotes:view" pageTitle="Quoting">
 *   <QuotingPage />
 * </PermissionPage>
 *
 * @example
 * // Require any of multiple permissions
 * <PermissionPage anyOf={["clients:view", "prospects:view"]} pageTitle="CRM">
 *   <CRMPage />
 * </PermissionPage>
 */
export function PermissionPage({
  permission,
  anyOf,
  allOf,
  children,
  pageTitle = "this page",
}: PermissionPageProps) {
  const { can, canAny, canAll, isLoading } = usePermissions();

  // Show loading state while permissions are being fetched
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse space-y-4 w-full max-w-md">
          <div className="h-4 bg-muted rounded w-3/4 mx-auto"></div>
          <div className="h-4 bg-muted rounded w-1/2 mx-auto"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  // Determine if user has required permission
  let hasAccess = true;
  if (permission) {
    hasAccess = can(permission);
  } else if (anyOf && anyOf.length > 0) {
    hasAccess = canAny(anyOf);
  } else if (allOf && allOf.length > 0) {
    hasAccess = canAll(allOf);
  }

  // User has access, render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // Access denied - show friendly message
  return (
    <div className="flex items-center justify-center min-h-[400px] p-8">
      <div className="max-w-md w-full space-y-6">
        <div className="flex justify-center">
          <div className="rounded-full bg-red-100 p-4">
            <Lock className="h-8 w-8 text-red-600" />
          </div>
        </div>

        <div className="text-center space-y-2">
          <h1 className="text-2xl font-semibold text-gray-900">Access Denied</h1>
          <p className="text-gray-600">
            You don't have permission to access {pageTitle}.
          </p>
          <p className="text-sm text-gray-500">
            Contact your administrator if you believe you should have access.
          </p>
        </div>

        <div className="flex justify-center">
          <Link href="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Return to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
